import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, FarmRiskType } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { LabelTooltip } from 'core/components/label-tooltip';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';
import { getPropertyTypeOptions, getDwellingConstructionTypeOptions } from 'common/helpers/farm-helpers';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import useStyles from './initial-risk-detail.styles';

// Form options constants
const ownershipStatusOptions = lookupsJson.farm.ownershipStatus;
const ctOptions = getDwellingConstructionTypeOptions(FarmRiskType.Fo4);
const rtOptions = lookupsJson.farm.dwellingRoofType;
const rsOptions = lookupsJson.roofShape;
const roOptions = lookupsJson.roofOriginal;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;

// FO-4 (Contents Only) should only show "Contents" option for Residence Type/Usage
const ruOptions = lookupsJson.farm.residenceUsage.filter((option) => option.id === 'C');
// FO-4 Structure Category is locked to "Home or Rental Unit" (FD) only — GitHub Issue: #29021
const propertyTypeOptions = getPropertyTypeOptions(FarmRiskType.Fo4);

const nfidOptions = [
  { id: '1', value: '1' },
  { id: '2', value: '2' },
  { id: '3', value: '3' },
  { id: '4', value: '4' }
];

const FO4RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const needMoreData = alerts.some((alert) => [5004, 50006].includes(alert.code));
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (needMoreData) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [needMoreData]);

  // Auto-set FO-4 locked defaults: propertyType='FD' (Home or Rental Unit), residenceUsage='C' (Contents Only)
  // These fields are disabled in the UI, so we must set them programmatically — GitHub Issue: #29154
  useEffect(() => {
    if (farmRisk?.building?.propertyType !== 'FD') {
      setFieldValue(`${buildingPath}.propertyType`, 'FD');
    }
    if (farmRisk?.building?.residenceUsage !== 'C') {
      setFieldValue(`${buildingPath}.residenceUsage`, 'C');
    }
  }, [farmRisk?.building?.propertyType, farmRisk?.building?.residenceUsage, buildingPath, setFieldValue]);

  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.propertyType"
        name={`${buildingPath}.propertyType`}
        mode="light"
        label="Structure Category*"
        options={propertyTypeOptions}
        disabled
      />
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.residenceUsage"
        name={`${buildingPath}.residenceUsage`}
        mode="light"
        label="Residence Type/Usage*"
        options={ruOptions}
        disabled
      />
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.constructionType"
        name={`${buildingPath}.constructionType`}
        mode="light"
        label="Construction Type"
        options={ctOptions}
      />
      <Grid item xs={6}>
        <LabelTooltip
          label="Solid Fuel Burning Appliances*"
          tooltip={{
            label: 'More Info',
            onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id="farmRisk.building.numSolidFuelBurningAppliances"
            name={`${buildingPath}.numSolidFuelBurningAppliances`}
            mode="light"
            format={{
              allowNegative: false
            }}
          />
        </LabelTooltip>
      </Grid>

      {/* Risk address automatically inherits from location */}

      <Grid item xs={12} className={classes.rentersInfoTextContainer}>
        <InfoIcon width={16} height={16} />
        <span className={classes.rentersInfoText}>
          Make sure to include a floor, unit, or building number if one exists for this rental.
        </span>
      </Grid>

      {needMoreData && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}

      {showAdditionalFields && (
        <>
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.yearBuilt"
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year Built"
          />
          <Field
            name={`${buildingPath}.ownershipStatus`}
            type="select"
            label="Ownership"
            mode="light"
            options={ownershipStatusOptions}
            xs={4}
          />
          <Field
            xs={4}
            type="select"
            options={nfidOptions}
            id="farmRisk.building.numFamiliesInDwelling"
            name={`${buildingPath}.numFamiliesInDwelling`}
            mode="light"
            label="Families living"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.primaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary Heating"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.secondaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.secondaryHeatingSource`}
            mode="light"
            label="Secondary Heating"
          />
          <RenovationFieldsForm basePath={farmRiskPath} checkboxXs={6} />
          <Field
            type="select"
            id="farmRisk.building.roofOriginal"
            name={`${buildingPath}.roofOriginal`}
            options={roOptions}
            mode="light"
            label="Original Roof?"
            xs={3}
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.roofYear"
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof Year"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofType"
            options={rtOptions}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofShape"
            options={rsOptions}
            name={`${buildingPath}.roofShape`}
            mode="light"
            label="Roof shape"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.latitude"
            name={`${buildingPath}.latitude`}
            mode="light"
            label="Latitude"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.longitude"
            name={`${buildingPath}.longitude`}
            mode="light"
            label="Longitude"
          />
        </>
      )}
    </Grid>
  );
};

export default FO4RiskDetail;
